import classNames from 'classnames'
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { useTimer } from '../../hooks/useTimer'
import { useTranslations } from '../../hooks/useTranslations'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { DotProgress } from './DotProgress'

export type SlideshowTimer = number | 'disabled'

// Common slideshow intervals in seconds
export const Timers: Record<string, SlideshowTimer> = {
  ShortInterval: 5, // single items, little text
  StandardInterval: 6, // images, little text, few items
  LongInterval: 9, // more text, more items
}

export enum SlideshowDotsColor {
  Variant1,
  Variant2,
  Variant3,
}

type Color = {
  baseColor: string
  selectedColor: string
}

const colors: Record<SlideshowDotsColor, Color> = {
  [SlideshowDotsColor.Variant1]: {
    baseColor: 'bg-shade bg-opacity-25 hover:bg-opacity-50 focus:bg-opacity-50',
    selectedColor: 'bg-shade',
  },
  [SlideshowDotsColor.Variant2]: {
    baseColor:
      'bg-neutral-contrast bg-opacity-10 hover:bg-primary hover:bg-opacity-50 focus:bg-opacity-50',
    selectedColor: 'bg-primary',
  },
  [SlideshowDotsColor.Variant3]: {
    baseColor:
      'bg-neutral bg-opacity-70 hover:bg-primary hover:bg-opacity-50 focus:bg-opacity-50',
    selectedColor: 'bg-primary',
  },
}

export interface ControlledSlideshowNavigationProps {
  color?: SlideshowDotsColor
  currentIndex: number
  hideNavigationButtons?: boolean
  numberOfDots: number
  onNavigate: (index: number) => void
  onNavigateNext: () => void
  onNavigatePrevious: () => void
  paused?: boolean
  /** Set a timer in seconds */
  timer?: SlideshowTimer
}

export const ControlledSlideshowNavigation: FC<ControlledSlideshowNavigationProps> = ({
  color = SlideshowDotsColor.Variant1,
  currentIndex,
  hideNavigationButtons = false,
  numberOfDots,
  onNavigate,
  onNavigateNext,
  onNavigatePrevious,
  paused,
  timer = 'disabled',
}) => {
  const translations = useTranslations()
  const startTime = useRef<number>()
  const timeLeft = useRef<number>()
  const countdown = useTimer()
  const onClickTimer = useTimer()
  const [clickTimerRunning, setClickTimerRunning] = useState(false)

  // Timer loop
  useEffect(() => {
    if (timer !== 'disabled') {
      startTime.current = new Date().getTime()
      timeLeft.current = timer * 1000

      countdown.start(() => {
        onNavigateNext()
      }, timeLeft.current)
    }
  }, [countdown, currentIndex, onNavigateNext, timer])

  // Handle pausing
  useEffect(() => {
    if (timer !== 'disabled') {
      const currentTime = new Date().getTime()

      if (paused) {
        // Stop the timer and calculate the time left when pausing
        countdown.stop()

        if (startTime.current && timeLeft.current) {
          timeLeft.current = startTime.current + timeLeft.current - currentTime
        }
      } else {
        // Set a new start time when unpausing
        startTime.current = currentTime

        countdown.start(() => {
          onNavigateNext()
        }, timeLeft.current ?? timer * 1000)
      }
    }
  }, [countdown, onNavigateNext, paused, timer])

  const onManualSlideChange = () => {
    setClickTimerRunning(true)
    onClickTimer.start(() => setClickTimerRunning(false), 1000)
    startTime.current = new Date().getTime()
  }

  const onClickDot = (index: number) => {
    onManualSlideChange()
    onNavigate(index)
  }

  const onClickPrevious = () => {
    if (currentIndex > 0) {
      onManualSlideChange()
      onNavigatePrevious()
    }
  }

  const onClickNext = () => {
    if (currentIndex < numberOfDots - 1) {
      onManualSlideChange()
      onNavigateNext()
    }
  }

  if (!numberOfDots) {
    return null
  }

  const dots: ReactNode[] = []

  for (let i = 0; i < numberOfDots; i++) {
    dots.push(
      <li key={i}>
        <button
          className={classNames(
            'relative block overflow-hidden rounded-5',
            'w-30 h-5 mx-15-1/2 transition-hover',
            colors[color].baseColor
          )}
          onClick={() => onClickDot(i)}
          type="button"
        >
          <span className="sr-only">Navigate to item {i}</span>

          <DotProgress
            active={i === currentIndex}
            color={colors[color].selectedColor}
            onClickTimer={onClickTimer}
            paused={paused && clickTimerRunning === false}
            timer={timer}
          />
        </button>
      </li>
    )
  }

  const navigationButtonClassName = classNames(
    'hidden min-w-0 flex-[100px] rounded-8 p-2 font-t-bold text-t-200 uppercase duration-200',
    'tb:block',
    'lg:text-t-300 lg:px-4 lg:py-3'
  )

  const navigationButtonActiveClassName =
    'text-secondary-darker hover:text-primary focus:ring focus:ring-primary/40 focus:outline-none'

  const navigationButtonInactiveClassName = 'text-neutral-medium cursor-default'

  const displayNavigationButtons =
    !hideNavigationButtons &&
    !isNullOrWhiteSpace(translations.previous) &&
    !isNullOrWhiteSpace(translations.next)

  return (
    // min-w-0 added to not overflow the parent element
    <div className="flex items-center min-w-0">
      {displayNavigationButtons && (
        <>
          <button
            className={classNames(navigationButtonClassName, {
              [navigationButtonActiveClassName]: currentIndex > 0,
              [navigationButtonInactiveClassName]: currentIndex === 0,
            })}
            onClick={onClickPrevious}
          >
            {translations.previous}
          </button>

          {/* Spacer div, will shrink to fit the parent */}
          <div className="w-60 flex-shrink"></div>
        </>
      )}

      <ul className="flex justify-center">{dots}</ul>

      {displayNavigationButtons && (
        <>
          {/* Spacer div, will shrink to fit the parent */}
          <div className="w-60 flex-shrink"></div>

          <button
            className={classNames(navigationButtonClassName, {
              [navigationButtonActiveClassName]: currentIndex < numberOfDots - 1,
              [navigationButtonInactiveClassName]: currentIndex === numberOfDots - 1,
            })}
            onClick={onClickNext}
          >
            {translations.next}
          </button>
        </>
      )}
    </div>
  )
}
